import { useEffect, useMemo, useRef, useState } from 'react';
import { useProvideReread } from '../shell/rereadAction';
import { Link } from 'react-router-dom';
import { useVault } from '../session/useVault';
import { VaultErrorSection } from '../session/VaultErrorSection';
import type { Arc, Status } from '../plan/arc';
import { planMove, withEvidence, withStatus, withVisibility } from '../plan/edits';
import { newStorylineEdit } from '../board/newStoryline';
import { addMember, removeMember } from '../plan/edits';
import { applyFilters, facetsOf, heldFilters, holdFilters, isFiltering, NO_FILTERS } from '../plan/filters';
import { isArchived } from '../plan/arc';
import type { Filters } from '../plan/filters';
import { chronological } from '../plan/chronology';
import { usePlan } from '../plan/usePlan';
import { Desk, readLayout, writeLayout } from '../board/Desk';
import { listOf } from '../entity/entity';
import { useFolders } from '../folders/FolderChip';
import { tagMarkOf } from '../folders/TagMark';
import { useTheme } from '../design/useTheme';
import type { Layout } from '../board/Desk';
import { useFilterRow } from '../shell/filterRow';
import { Loading } from '../shell/Loading';

export function BoardRoute() {
  const { state, reload, write, commit } = useVault();
  // The plan loads itself from the shared store — a queued write shows there at
  // once and re-reads when it settles, so nothing here asks for a read.
  const { plan: planState } = usePlan();
  const plan = planState.status === 'ready' ? planState.plan : null;
  const failed = planState.status === 'error';
  const progress = planState.status === 'loading' ? planState : { done: 0, total: 0 };

  const move = async (name: string, toStatus: Status, beforeName: string | null) => {
    if (!plan || state.status !== 'ready') return;
    const edits = planMove(plan.arcs, name, toStatus, beforeName);
    if (edits.length === 0) return;

    write(edits);
  };

  const setStatus = async (arc: Arc, status: Status, evidence?: string) => {
    if (state.status !== 'ready') return;
    // One click, one file, and no run of gestures behind it — so it goes now.
    // A reorder renumbers a band and keeps the debounce.
    const change = (t: string) => (evidence ? withEvidence(withStatus(t, status), evidence) : withStatus(t, status));
    await commit([{ path: arc.path, text: change(arc.raw), change }]);
  };

  /** Filing away, and taking back out. A sibling of [setStatus] rather than a
   *  case of it: it writes `visibility:` and leaves `status:` where it is. */
  const setArchived = async (arc: Arc, archived: boolean) => {
    if (state.status !== 'ready') return;
    const change = (t: string) => withVisibility(t, archived ? 'archived' : 'visible');
    await commit([{ path: arc.path, text: change(arc.raw), change }]);
  };

  /** One position up or down inside the arc's own band. Reordering is always
   *  a move relative to a neighbour, which is the vocabulary planMove speaks. */
  const nudge = (arc: Arc, direction: -1 | 1) => {
    if (!plan) return;
    const band = plan.arcs
      .filter((a) => a.status === arc.status)
      .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));
    const at = band.findIndex((a) => a.name === arc.name);
    const target = at + direction;
    if (at < 0 || target < 0 || target >= band.length) return;
    // Moving down means landing before the arc AFTER the neighbour.
    const before = direction < 0 ? band[target] : band[target + 1];
    void move(arc.name, arc.status, before?.name ?? null);
  };

  /** A storyline is an arc, so grouping writes one more arc file and lets the
   *  importer mint it. Members leave top level as soon as the file is there —
   *  a grouped arc renders inside its storyline or at top level, never both. */
  const group = async (name: string, members: Arc[]) => {
    const edit = newStorylineEdit(name, members);
    if (!edit) return;
    await commit([edit]);
  };

  /** Joining or leaving a storyline is a write to the HOLDER's file — the
   *  member never records who holds it, which is why membership is declared
   *  in one place rather than inferred from two. */
  const membership = async (storyline: Arc, member: Arc, join: boolean) => {
    const edit = join ? addMember(storyline, member.path) : removeMember(storyline, member.path);
    if (!edit) return;
    await commit([edit]);
  };

  if (state.status === 'disconnected') {
    return (
      <section className="placeholder">
        <h1>Not connected</h1>
        <p>
          <Link to="/connect">Connect this browser</Link> to read your vault.
        </p>
      </section>
    );
  }

  if (state.status === 'error') return <VaultErrorSection state={state} reload={reload} />;

  if (failed) {
    return (
      <section className="placeholder">
        <h1>Could not read the plan</h1>
        <button type="button" onClick={reload}>
          Try again
        </button>
      </section>
    );
  }

  if (state.status === 'loading' || !plan) {
    return (
      <Loading
        title={state.status === 'loading' ? 'Opening the vault…' : 'Reading the plan…'}
        progress={progress.total > 0 ? progress.done / progress.total : null}
      />
    );
  }

  // No arcs is not an error, and not an invitation to file. Arcs are minted
  // where thoughts are captured, so the empty state says where to go — and
  // the board's chrome stays rendered, because the page is not broken.
  if (plan.arcs.length === 0) {
    return (
      <>
        <section className="empty">
          <ArcMark />
          <h1>No arcs in this vault yet.</h1>
          <p className="t-bodyProse">
            An arc starts as something you said. Record on the phone, mint it in Studio, and this
            board reads the same files seconds later.
          </p>
        </section>
      </>
    );
  }

  return (
    <BoardView
      conflicted={plan.board.conflictCount > 0}
      onReload={reload}
      onSetStatus={setStatus}
      onSetArchived={setArchived}
      onNudge={nudge}
      onGroup={group}
      onMembership={membership}
      allArcs={plan.arcs}
    />
  );
}

function BoardView({
  conflicted,
  onReload,
  onSetStatus,
  onSetArchived,
  onNudge,
  onGroup,
  onMembership,
  allArcs,
}: {
  /** Conflict copies the sync minted, which the plan leaves out. */
  conflicted: boolean;
  onReload: () => void;
  onSetStatus: (arc: Arc, status: Status, evidence?: string) => void;
  onSetArchived: (arc: Arc, archived: boolean) => void;
  onNudge: (arc: Arc, direction: -1 | 1) => void;
  onGroup: (name: string, members: Arc[]) => Promise<void>;
  onMembership: (storyline: Arc, member: Arc, join: boolean) => Promise<void>;
  allArcs: Arc[];
}) {
  useProvideReread(onReload);
  const folders = useFolders();
  const { theme } = useTheme();
  const [layout, setLayout] = useState<Layout>(readLayout);
  const [filters, setFiltersState] = useState<Filters>(heldFilters);
  const setFilters = (next: Filters | ((current: Filters) => Filters)) =>
    setFiltersState((current) => {
      const value = typeof next === 'function' ? next(current) : next;
      holdFilters(value);
      return value;
    });
  /**
   * Filed-away arcs, off the plan.
   *
   * Not one of [Filters]: those are ADDITIVE and every one of them narrows, so
   * a control that WIDENS the set cannot live among them without breaking the
   * only reading that makes them trustworthy. Same shape as the thoughts feed's
   * hidden ones, and for the same reason.
   */
  const [showArchived, setShowArchived] = useState(false);
  const queryField = useRef<HTMLInputElement>(null);
  const live = useMemo(
    () => (showArchived ? allArcs : allArcs.filter((arc) => !isArchived(arc))),
    [allArcs, showArchived],
  );
  const hasArchived = useMemo(() => allArcs.some(isArchived), [allArcs]);
  const shown = useMemo(
    () => (isFiltering(filters) ? applyFilters(live, filters) : live),
    [live, filters],
  );
  // Ordered here rather than inside the layout, so the keyboard walks exactly
  // the run that is drawn.
  const headed = useMemo(() => chronological(shown, new Date()), [shown]);
  const facets = useMemo(() => facetsOf(live), [live]);

  // Anything other than the resting defaults keeps the controls on screen —
  // including a widening, which is otherwise invisible.
  const narrowed = isFiltering(filters) || showArchived;
  const filterRow = useFilterRow('plan', narrowed);

  // `/` reaches for the filter from anywhere on the plan, opening it if need be.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      if (e.key !== '/') return;
      e.preventDefault();
      if (!filterRow.open) filterRow.toggle();
      requestAnimationFrame(() => queryField.current?.focus());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [filterRow]);

  const chooseLayout = (next: Layout) => {
    writeLayout(next);
    setLayout(next);
  };

  return (
    <div className={`plan layout-${layout}`}>
      <div className="plan-top">
        <span className="plan-top-spacer" />
        <button
          type="button"
          className={`head-icon${narrowed ? ' is-narrowed' : ''}`}
          aria-label={filterRow.open ? 'Hide filters' : 'Filter the plan'}
          aria-pressed={filterRow.open}
          onClick={filterRow.toggle}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <circle cx="5" cy="12" r="1.7" />
            <circle cx="12" cy="12" r="1.7" />
            <circle cx="19" cy="12" r="1.7" />
          </svg>
        </button>
        <div className="plan-switch" role="group" aria-label="How the plan is drawn">
          {(['recent', 'shelves'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={layout === option}
              onClick={() => chooseLayout(option)}
            >
              {option === 'recent' ? 'Recent' : 'Shelves'}
            </button>
          ))}
        </div>
      </div>

      {filterRow.open && (
        <div className="plan-filters">
          <input
            ref={queryField}
            className="filter-field"
            value={filters.query}
            placeholder="/ filter"
            onChange={(e) => setFilters((f) => ({ ...f, query: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setFilters(NO_FILTERS);
                e.currentTarget.blur();
              }
            }}
          />
          <Facet
            label="area"
            value={filters.area}
            options={facets.areas}
            onPick={(v) => setFilters((f) => ({ ...f, area: v }))}
          />
          <Facet
            label="tag"
            value={filters.tag}
            options={facets.tags}
            onPick={(v) => setFilters((f) => ({ ...f, tag: v }))}
          />
          {/* A mode rather than a facet: "just the ones asking" in one tap. */}
          <button
            type="button"
            className="filter-chip"
            aria-pressed={filters.status === 'needs-input'}
            onClick={() =>
              setFilters((f) => ({ ...f, status: f.status === 'needs-input' ? null : 'needs-input' }))
            }
          >
            needs you
          </button>
          <button
            type="button"
            className="filter-chip"
            aria-pressed={filters.inFlight}
            onClick={() => setFilters((f) => ({ ...f, inFlight: !f.inFlight }))}
          >
            in flight
          </button>
          <button
            type="button"
            className="filter-chip"
            aria-pressed={filters.recent}
            onClick={() => setFilters((f) => ({ ...f, recent: !f.recent }))}
          >
            touched this week
          </button>
          {/* Last, and set apart from the ones above it: those narrow what is
              on the plan, this brings back what has been taken off it. */}
          {hasArchived && (
            <button
              type="button"
              className="filter-chip"
              aria-pressed={showArchived}
              title="Filed away — off the plan whatever their status still says"
              onClick={() => setShowArchived(!showArchived)}
            >
              archived
            </button>
          )}
          {isFiltering(filters) && (
            <button type="button" className="filter-chip is-clear" onClick={() => setFilters(NO_FILTERS)}>
              Clear
            </button>
          )}
          {conflicted && (
            // Said where the plan's other quiet facts are: these are real files
            // the sync minted, and pretending they are absent is how they stay
            // unresolved.
            <span className="plan-note">Conflict copies are left out.</span>
          )}
        </div>
      )}

      <Desk
        arcs={shown}
        layout={layout}
        headed={headed}
        onSetStatus={onSetStatus}
        onSetArchived={onSetArchived}
        onNudge={onNudge}
        onGroup={onGroup}
        onMembership={onMembership}
        allArcs={allArcs}
        markOf={(arc) => tagMarkOf(listOf(arc.frontMatter['tags']), folders, theme)}
      />
    </div>
  );
}

/** A one-of picker over what the vault actually declares. Picking the value
 *  that is already set clears it, so the control is its own off switch. */
function Facet({
  label,
  value,
  options,
  onPick,
}: {
  label: string;
  value: string | null;
  options: string[];
  onPick: (value: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  if (options.length === 0) return null;

  return (
    <span className="facet">
      <button
        type="button"
        className="filter-chip"
        aria-pressed={value !== null}
        onClick={() => setOpen((o) => !o)}
      >
        {value ?? label} ⌄
      </button>
      {open && (
        <div className="facet-menu" onMouseLeave={() => setOpen(false)}>
          {options.map((option) => (
            <button
              key={option}
              type="button"
              className={option === value ? 'on' : undefined}
              onClick={() => {
                onPick(option === value ? null : option);
                setOpen(false);
              }}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

function ArcMark() {
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true" className="empty-mark">
      <circle cx="11" cy="15" r="8" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="20" cy="15" r="8" fill="none" stroke="currentColor" strokeWidth="1.6" opacity="0.5" />
    </svg>
  );
}
